import { securePost, secureGet, secureDelete } from "../../Config/ApiConfig";
import { removeNullUndefinedWithReduce } from "../../Utils/javascript";
import { deleteConversation } from "../Messages/MessagesServices";

/**
 * Fetch virtual channels for an agent.
 * @param {string} agentId - The agent ID
 * @returns {Promise<Array>} Array of virtual channels
 */
export const getVirtualChannels = async (agentId) => {
    try {
        const filters = removeNullUndefinedWithReduce({
            agent_id: agentId,
            is_virtual: true
        });
        const { data } = await secureGet(
            `/channels?page=1&page_size=10&sort_by=created_at&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        if (data.data && data.data.length > 0) {
            return data.data;
        }
        return [];
    } catch (error) {
        throw new Error(`Failed to fetch virtual channels: ${error.message}`);
    }
};

/**
 * Create a virtual channel for an agent.
 * @param {string} agentId - The agent ID
 * @param {string} channelType - The channel type (e.g., 'facebook_page')
 * @param {string} name - Optional name for the channel
 * @returns {Promise<Object>} The created virtual channel
 */
export const createVirtualChannel = async (agentId, channelType, name = null) => {
    try {
        const channelData = removeNullUndefinedWithReduce({
            channel_type: channelType,
            name: name,
        });

        const { data } = await securePost(`/agents/${agentId}/virtual-channels`, channelData);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to create virtual channel: ${error.message}`);
    }
};

/**
 * Get or create a virtual channel for an agent.
 * Uses the agent's first linked channel type, or defaults to 'facebook_page'.
 * @param {Object} agent - The agent object with id, name, and channels
 * @returns {Promise<Object>} The virtual channel
 */
export const getOrCreateVirtualChannel = async (agent) => {
    try {
        // Check for existing virtual channels
        const existingChannels = await getVirtualChannels(agent.id);
        if (existingChannels.length > 0) {
            return existingChannels[0];
        }

        // Derive channel type from agent's linked channels, fallback to facebook_page
        const nonVirtualChannels = (agent.channels || []).filter(c => !c.is_virtual);
        const channelType = nonVirtualChannels[0]?.channel_type || 'facebook_page';
        const channelName = `${agent.name} - Test Channel`;

        // Create new virtual channel
        return await createVirtualChannel(agent.id, channelType, channelName);
    } catch (error) {
        throw new Error(`Failed to get or create virtual channel: ${error.message}`);
    }
};

/**
 * Fetch virtual contacts for an agent (via its virtual channel).
 * @param {string} agentId - The agent ID
 * @returns {Promise<Array>} Array of virtual contacts
 */
export const getVirtualContacts = async (agentId) => {
    try {
        // First get the virtual channel for this agent
        const virtualChannels = await getVirtualChannels(agentId);
        if (virtualChannels.length === 0) {
            return [];
        }

        const virtualChannelId = virtualChannels[0].id;
        const filters = removeNullUndefinedWithReduce({ channel_id: virtualChannelId });
        const { data } = await secureGet(
            `/contacts?page=1&page_size=10&sort_by=created_at&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        if (data.data && data.data.length > 0) {
            return data.data;
        }
        return [];
    } catch (error) {
        throw new Error(`Failed to fetch virtual contacts: ${error.message}`);
    }
};

/**
 * Create a virtual contact under a virtual channel.
 * @param {string} channelId - The virtual channel ID
 * @param {Object} virtualContactData - Contact data overrides
 * @returns {Promise<Object>} The created virtual contact
 */
export const createVirtualContact = async (channelId, virtualContactData = {}) => {
    try {
        const defaultContactData = {
            first_name: "Test",
            last_name: "Customer",
            email: "test.customer@example.com",
            phone: "1234567890",
            locale: "en_US",
            timezone: -new Date().getTimezoneOffset() / 60,
            gender: "male",
            note: "Virtual test customer created for agent testing",
            ...virtualContactData
        };

        const { data } = await securePost(`/channels/${channelId}/virtual-contacts`, defaultContactData);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to create virtual contact: ${error.message}`);
    }
};

/**
 * Get conversation for a virtual contact.
 * @param {string} contactId - The contact ID
 * @returns {Promise<Object>} The conversation
 */
export const getVirtualContactConversation = async (contactId) => {
    try {
        const { data } = await secureGet(`/contacts/${contactId}/conversation`);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to get virtual contact conversation: ${error.message}`);
    }
};

/**
 * Get existing virtual conversation for an agent.
 * @param {string} agentId - The agent ID
 * @returns {Promise<Object|null>} The virtual conversation or null
 */
export const getExistingVirtualConversation = async (agentId) => {
    try {
        // Get virtual channel first
        const virtualChannels = await getVirtualChannels(agentId);
        if (virtualChannels.length === 0) {
            return null;
        }

        const virtualChannelId = virtualChannels[0].id;
        const filters = removeNullUndefinedWithReduce({ channel_id: virtualChannelId });
        const { data } = await secureGet(
            `/conversations?page=1&page_size=5&sort_by=updated_time&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        if (data.data && data.data.length > 0) {
            return data.data[0];
        }
        return null;
    } catch (error) {
        throw new Error(`Failed to fetch virtual conversations: ${error.message}`);
    }
};

/**
 * Start an agent testing conversation.
 * Creates virtual channel and contact if needed.
 * @param {Object} agent - The agent object
 * @returns {Promise<Object>} The conversation
 */
export const startAgentTestingConversation = async (agent) => {
    try {
        // Step 1: Ensure virtual channel exists
        const virtualChannel = await getOrCreateVirtualChannel(agent);

        // Step 2: Check if virtual contacts exist for this channel
        let virtualContacts = await getVirtualContacts(agent.id);

        // Step 3: If no virtual contacts exist, create a default one
        if (virtualContacts.length === 0) {
            const newVirtualContact = await createVirtualContact(virtualChannel.id);
            virtualContacts = [newVirtualContact];
        }

        // Step 4: Use the first virtual contact to get/create conversation
        const virtualContact = virtualContacts[0];
        const conversation = await getVirtualContactConversation(virtualContact.id);

        return conversation;
    } catch (error) {
        throw new Error(`Failed to start test conversation: ${error.message}`);
    }
};

/**
 * Delete the agent testing conversation.
 * @param {string} agentId - The agent ID
 * @returns {Promise<boolean>} True if deleted, false if not found
 */
export const deleteAgentTestingConversation = async (agentId) => {
    try {
        const existing = await getExistingVirtualConversation(agentId);
        if (existing?.id) {
            await deleteConversation(existing.id);
            return true;
        }
        return false;
    } catch (error) {
        throw new Error(`Failed to delete test conversation: ${error.message}`);
    }
};

/**
 * Send a message as a virtual contact to the agent.
 * @param {string} virtualContactId - The virtual contact ID
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} The sent message
 */
export const sendAgentTestingMessage = async (virtualContactId, messageData) => {
    try {
        const { data } = await securePost(
            `/contacts/${virtualContactId}/messages`,
            messageData
        );
        return data;
    } catch (error) {
        throw new Error(`Failed to send message to virtual contact: ${error.message}`);
    }
};

/**
 * Delete a virtual contact.
 * @param {string} contactId - The contact ID
 * @returns {Promise<Object>} The response data
 */
export const deleteVirtualContact = async (contactId) => {
    try {
        const { data } = await secureDelete(`/contacts/${contactId}`);
        return data;
    } catch (error) {
        throw new Error(`Failed to delete virtual contact: ${error.message}`);
    }
};